import type { FirebaseApp } from 'firebase/app';
import { collection, connectFirestoreEmulator, deleteDoc, doc, getDocs, getFirestore, orderBy, query, serverTimestamp, setDoc, type EmulatorMockTokenOptions, type Firestore } from 'firebase/firestore';
import { validateCharacterBuild, type CharacterBuild } from '../../shared/game/character-build.js';
import { characterOptions } from '../../content/characters/character-options.js';
import { humanoidBeard, humanoidBeardAllowed } from '../../content/characters/rigs/humanoid-parts.js';

/** Characters an account may keep. The security rules enforce ownership; this keeps the list readable. */
export const MAX_SAVED_CHARACTERS = 12;

/**
 * The signed-in account's saved characters, in Firestore under `accounts/{uid}/characters/{id}`.
 * Only that account can read or write them (firestore.rules). The game server never reads them:
 * the browser brings the chosen builds when entering a room and the server checks them against the rules.
 */
export class CharacterStore {
  private firestore: Firestore | null = null;
  private uid: string | null = null;
  private cache: CharacterBuild[] | null = null;
  private listeners = new Set<(characters: CharacterBuild[]) => void>();
  /** Called once the account service has its Firebase app. `emulatorHost` (`host:port`) is the local demo emulator only. */
  attach(app: FirebaseApp, emulatorHost: string | null = null, mockUserToken?: EmulatorMockTokenOptions) {
    this.firestore = getFirestore(app);
    if (emulatorHost) {
      const [host, port] = emulatorHost.split(':');
      connectFirestoreEmulator(this.firestore, host, Number(port), mockUserToken ? { mockUserToken } : {});
    }
  }
  private get db() { if (!this.firestore) throw new Error('Character storage is unavailable.'); return this.firestore; }
  setAccount(uid: string | null) { if (uid === this.uid) return; this.uid = uid; this.cache = null; if (uid) void this.list().catch(() => {}); else this.emit([]); }
  get signedIn() { return this.uid !== null; }
  onChange(listener: (characters: CharacterBuild[]) => void) { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  async list(): Promise<CharacterBuild[]> {
    const uid = this.requireAccount();
    if (this.cache) return [...this.cache];
    const snapshot = await getDocs(query(collection(this.db, 'accounts', uid, 'characters'), orderBy('createdAt')));
    if (uid !== this.uid) return [];
    // A record that no longer passes the rules (content changed since it was saved) is left out rather than offered.
    const characters = snapshot.docs.flatMap(entry => {
      try {
        let build = entry.data().build;
        const look = build?.look;
        // Keep older characters selectable after a facial-hair restriction changes; saving persists the legal choice.
        if (typeof look?.body === 'string' && typeof look.parts?.beard === 'string' && !humanoidBeardAllowed(look.body, look.parts.beard)) {
          build = { ...build, look: { ...look, parts: { ...look.parts, beard: humanoidBeard(look.body) } } };
        }
        return [validateCharacterBuild({ ...build, id: entry.id }, characterOptions)];
      } catch { return []; }
    });
    this.cache = characters; this.emit(characters); return [...characters];
  }
  async save(build: CharacterBuild): Promise<CharacterBuild> {
    const uid = this.requireAccount(), existing = await this.list();
    const id = build.id || doc(collection(this.db, 'accounts', uid, 'characters')).id;
    if (!existing.some(character => character.id === id) && existing.length >= MAX_SAVED_CHARACTERS) throw new Error(`You can keep up to ${MAX_SAVED_CHARACTERS} characters. Delete one first.`);
    const clean = validateCharacterBuild({ ...build, id }, characterOptions);
    const { id: _id, ...stored } = clean;
    const previous = existing.find(character => character.id === id);
    await setDoc(doc(this.db, 'accounts', uid, 'characters', id), { build: stored, updatedAt: serverTimestamp(), ...(previous ? {} : { createdAt: serverTimestamp() }) }, { merge: true });
    this.cache = previous ? existing.map(character => character.id === id ? clean : character) : [...existing, clean];
    this.emit(this.cache); return clean;
  }
  async remove(id: string) {
    const uid = this.requireAccount();
    await deleteDoc(doc(this.db, 'accounts', uid, 'characters', id));
    this.cache = (this.cache ?? []).filter(character => character.id !== id); this.emit(this.cache);
  }
  private requireAccount() { if (!this.uid) throw new Error('Sign in to keep characters.'); return this.uid; }
  private emit(characters: CharacterBuild[]) { for (const listener of this.listeners) listener([...characters]); }
}
